import React from "react";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import ScienceIcon from "@mui/icons-material/Science";

import UserUpdateForm from "../components/UserUpdateForm";
import CustomForm from "../../../helper-components/form/CustomForm";
import PaperFragment from "../../../helper-components/misc/PaperFragment";
import TransparentFragment from "../../../helper-components/misc/TransparentFragment";
import StackedHeader from "../../../helper-components/misc/StackedHeader";
import NavigationFooter from "../../../helper-components/misc/NavigationFooter";

import { getLanguage } from "../../../config/language/language";

/**
 * An overview page for a user to see their account info.
 */
export default function Account(props) {
    const accountSubtitle = getLanguage().ACCOUNT_SUBTITLE.replace("<username>", props.user.username);

    const userData = props?.user?.me || {};
    const { id = "N/A", username = "N/A", email = "N/A", role = {} } = userData;

    const [showSensitive, setShowSensitive] = React.useState(false);

    // Hide or show sensitive info.
    const formattedEmail = showSensitive ? email : email.replace(/[a-z0-9-_]/gi, "*");
    const formattedRole = role?.role || "N/A";

    const showSensitiveInfo = () => {
        setShowSensitive(!showSensitive);
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <CustomForm title={getLanguage().ACCOUNT_TITLE} subtitle={accountSubtitle} icon={<PersonIcon />} />
                </Grid>

                {/* Account info. */}
                <Grid item xs={12}>
                    <PaperFragment
                        title={
                            <StackedHeader
                                {...props}
                                title="User details"
                                icons={[{ icon: showSensitive ? <VisibilityIcon /> : <VisibilityOffIcon />, tooltip: "Show / hide sensitive info", onClick: showSensitiveInfo }]}
                            />
                        }
                        value={
                            <Typography className="pre-wrap">
                                {getLanguage()
                                    .ACCOUNT_TEXT_1.replace("<id>", id)
                                    .replace("<username>", username)
                                    .replace("<role>", formattedRole)
                                    .replace("<email>", formattedEmail)}
                            </Typography>
                        }
                    />
                </Grid>

                <Grid item xs={12}>
                    <CustomForm title={getLanguage().USER_UPDATE_TITLE} subtitle={getLanguage().USER_UPDATE_SUBTITLE} form={<UserUpdateForm {...props} />} />
                </Grid>

                <Grid item xs={12}>
                    <CustomForm
                        title="Experimental Features"
                        subtitle="Enable or disable experimental features that do not directly affect the core functionality of the application or are still in development."
                        icon={<ScienceIcon />}
                        form={
                            <TransparentFragment
                                value={
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={props.browser?.showTranslations || false}
                                                onChange={(e) => props.browser?.setShowTranslations?.(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Show translation pages"
                                    />
                                }
                            />
                        }
                    />
                </Grid>

                <NavigationFooter
                    rows={1}
                    options={[
                        {
                            path: `/home`,
                            title: `Home`,
                            icon: <HomeIcon />
                        },
                        {
                            path: `/about`,
                            title: `About`,
                            icon: <InfoIcon />
                        }
                    ]}
                />
            </Grid>
        </Container>
    );
}
